import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import PromoBanner from "@/components/site/PromoBanner";
import StickyBuyBar from "@/components/site/StickyBuyBar";
import Playground from "@/components/site/Playground";
import SEO from "@/components/seo/SEO";
import { ogUrl } from "@/data/site";
import { getProduct, products } from "@/data/products";
import { buyProduct } from "@/data/tiers";

const SectionRule = () => <div className="h-px bg-[#E7E7E2]" />;

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProduct(slug);
  const [buying, setBuying] = useState(false);

  const onBuy = async () => {
    setBuying(true);
    try {
      await buyProduct(product.slug);
    } catch {
      toast.error("Could not start checkout. Please try again.");
      setBuying(false);
    }
  };

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const isAvailable = product.status === "available";
  const others = products.filter((p) => p.slug !== product.slug).slice(0, 3);

  return (
    <main
      data-testid="product-detail-page"
      className="min-h-screen bg-[#F7F7F4] flex flex-col pb-20 md:pb-0"
    >
      <SEO
        title={product.name}
        description={product.tagline}
        path={`/products/${product.slug}`}
        ogImage={ogUrl(product.slug)}
      />
      <PromoBanner />
      <Header />

      {/* Breadcrumb / back */}
      <div className="bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5">
          <Link
            to="/products"
            data-testid="back-to-products"
            className="inline-flex items-center gap-1 font-manrope text-[13px] text-[#111111]/65 hover:text-[#111111] transition-colors"
          >
            <ChevronLeft size={14} />
            All products
          </Link>
        </div>
      </div>

      {/* Hero block */}
      <section className="bg-[#F7F7F4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4">
                <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#111111]/45">
                  Kit · {product.no}
                </span>
                <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/45">
                  {product.category}
                </span>
                {isAvailable ? (
                  <span
                    data-testid="product-status-available"
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#2563EB]"
                  >
                    Available now
                  </span>
                ) : (
                  <span
                    data-testid="product-status-coming-soon"
                    className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/55"
                  >
                    Coming soon
                  </span>
                )}
              </div>

              <h1
                data-testid="product-name"
                className="mt-6 font-sora text-[44px] sm:text-[58px] lg:text-[72px] font-extrabold tracking-tight leading-[1.02] text-[#111111]"
              >
                {product.name}.
              </h1>
              <p className="mt-6 max-w-xl font-manrope text-[17px] md:text-[19px] leading-[1.65] text-[#111111]/70">
                {product.tagline}
              </p>

              {product.description && (
                <p className="mt-5 max-w-xl font-manrope text-[15.5px] md:text-[16px] leading-[1.75] text-[#111111]/65">
                  {product.description}
                </p>
              )}

              <div className="mt-9 flex flex-wrap items-center gap-3">
                {isAvailable ? (
                  <button
                    type="button"
                    onClick={onBuy}
                    disabled={buying}
                    data-testid="btn-product-buy"
                    className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {buying ? (
                      <>
                        Opening checkout…
                        <Loader2 size={16} strokeWidth={2.25} className="animate-spin" />
                      </>
                    ) : (
                      <>
                        {product.price ? `Buy for ${product.price}` : "View product"}
                        <ArrowUpRight size={16} strokeWidth={2.25} />
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to="/#free-tools"
                    data-testid="btn-product-notify"
                    className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
                  >
                    Get notified
                    <ArrowUpRight size={16} strokeWidth={2.25} />
                  </Link>
                )}
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
                >
                  Ask a question
                </Link>
              </div>
            </div>

            {/* Right info card */}
            <div className="lg:col-span-5">
              <aside className="bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8 sticky top-24">
                <div className="rounded-xl overflow-hidden border border-[#E7E7E2] mb-6">
                  <img
                    src={`/images/products/${product.slug}.jpg`}
                    alt={`${product.name} cover`}
                    data-testid="product-cover-image"
                    className="w-full"
                  />
                </div>
                <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/45">
                  Quick details
                </div>
                <dl className="mt-5 divide-y divide-[#E7E7E2]">
                  <div className="py-3 flex items-baseline justify-between gap-4">
                    <dt className="font-manrope text-[13px] text-[#111111]/60">
                      Format
                    </dt>
                    <dd className="font-manrope text-[14px] text-[#111111] text-right">
                      Digital · Instant
                    </dd>
                  </div>
                  {product.audience && (
                    <div className="py-3 flex items-baseline justify-between gap-4">
                      <dt className="font-manrope text-[13px] text-[#111111]/60">
                        Audience
                      </dt>
                      <dd className="font-manrope text-[14px] text-[#111111] text-right">
                        {product.audience}
                      </dd>
                    </div>
                  )}
                  {product.access && (
                    <div className="py-3 flex items-baseline justify-between gap-4">
                      <dt className="font-manrope text-[13px] text-[#111111]/60">
                        Access
                      </dt>
                      <dd className="font-manrope text-[14px] text-[#111111] text-right">
                        {product.access}
                      </dd>
                    </div>
                  )}
                  {product.price && (
                    <div className="py-3 flex items-baseline justify-between gap-4">
                      <dt className="font-manrope text-[13px] text-[#111111]/60">
                        Price
                      </dt>
                      <dd className="font-sora text-[18px] font-bold text-[#111111] tracking-tight text-right">
                        {product.price}
                      </dd>
                    </div>
                  )}
                </dl>
                {product.price && (
                  <div
                    data-testid="product-reassurance"
                    className="mt-6 pt-5 border-t border-[#E7E7E2] flex flex-wrap items-center gap-x-3 gap-y-2 font-mono text-[10px] tracking-[0.14em] uppercase text-[#111111]/65"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2563EB]" /> Instant download
                    </span>
                    <span className="text-[#111111]/25">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2563EB]" /> Or included with Studio
                    </span>
                    <span className="text-[#111111]/25">·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-[#2563EB]" /> Cancel anytime
                    </span>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>
      </section>

      {/* Playground — only for the live AI Writing Kit */}
      {isAvailable && product.slug === "ai-writing-kit" && <Playground />}

      {/* Features + Inside */}
      {(product.features || product.inside) && (
        <section className="bg-white border-y border-[#E7E7E2]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              {product.features && (
                <div className="lg:col-span-6">
                  <div className="overline">— What you get</div>
                  <h2 className="mt-5 font-sora text-[32px] md:text-[40px] font-bold text-[#111111] tracking-tight leading-tight">
                    Inside the kit.
                  </h2>
                  <ul className="mt-8 divide-y divide-[#E7E7E2]">
                    {product.features.map((f, i) => (
                      <li
                        key={f}
                        className="flex items-baseline gap-5 py-4 font-manrope text-[16px] md:text-[17px] text-[#111111]"
                      >
                        <span className="font-mono text-xs text-[#111111]/40 w-6">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="flex-1">{f}</span>
                        <span className="text-[#2563EB] font-mono text-sm">+</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {product.inside && (
                <div className="lg:col-span-6">
                  <div className="overline">— Categories</div>
                  <h2 className="mt-5 font-sora text-[32px] md:text-[40px] font-bold text-[#111111] tracking-tight leading-tight">
                    Organized by use.
                  </h2>
                  <div className="mt-8 grid grid-cols-1 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-xl overflow-hidden">
                    {product.inside.map((s) => (
                      <div key={s.label} className="bg-white px-5 py-4">
                        <div className="font-sora text-[15px] font-semibold text-[#111111]">
                          {s.label}
                        </div>
                        <div className="mt-1 font-manrope text-[13px] text-[#111111]/60">
                          {s.note}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* What's inside the download — actual zip contents */}
      {product.files && (
        <section
          data-testid="product-files-section"
          className="bg-[#F7F7F4] border-b border-[#E7E7E2]"
        >
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
              <div className="lg:col-span-4">
                <div className="overline">— The download</div>
                <h2 className="mt-5 font-sora text-[32px] md:text-[40px] font-bold text-[#111111] tracking-tight leading-tight">
                  Exactly what you get.
                </h2>
                <p className="mt-4 font-manrope text-[15px] md:text-[16px] text-[#111111]/65 leading-relaxed">
                  No mystery box. This is the full contents of the zip that
                  lands in your inbox right after purchase — open everything
                  in any text editor, Notion, or Obsidian.
                </p>
              </div>
              <div className="lg:col-span-8">
                <div className="bg-[#111111] rounded-2xl overflow-hidden">
                  <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-white/10">
                    <span
                      data-testid="product-zip-name"
                      className="font-mono text-[12px] text-white/85 truncate"
                    >
                      {product.zipName}
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#60A5FA] shrink-0">
                      {product.files.length} items
                    </span>
                  </div>
                  <ul data-testid="product-files-list" className="divide-y divide-white/[0.06]">
                    {product.files.map((f) => (
                      <li
                        key={f.name}
                        className="px-6 py-3.5 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6"
                      >
                        <span className="font-mono text-[13px] text-white shrink-0 sm:w-[290px] truncate">
                          {f.name}
                        </span>
                        <span className="font-manrope text-[13px] text-white/55 leading-relaxed">
                          {f.desc}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {product.faq && product.faq.length > 0 && (
        <section className="bg-[#F7F7F4]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
              <div className="lg:col-span-4">
                <div className="overline">— Questions</div>
                <h2 className="mt-5 font-sora text-[32px] md:text-[40px] font-bold text-[#111111] tracking-tight leading-tight">
                  Common questions.
                </h2>
              </div>
              <div className="lg:col-span-8">
                <ul className="divide-y divide-[#E7E7E2] border-y border-[#E7E7E2]">
                  {product.faq.map((q, i) => (
                    <li key={q.q} className="py-7">
                      <div className="flex items-baseline gap-5">
                        <span className="font-mono text-xs text-[#111111]/40">
                          0{i + 1}
                        </span>
                        <div>
                          <div className="font-sora text-[18px] md:text-[19px] font-semibold text-[#111111]">
                            {q.q}
                          </div>
                          <p className="mt-2 font-manrope text-[15px] md:text-[16px] text-[#111111]/70 leading-relaxed">
                            {q.a}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other products */}
      {others.length > 0 && (
        <section className="bg-white border-t border-[#E7E7E2]">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <div className="overline">— Also from Vaymilo</div>
                <h2 className="mt-3 font-sora text-[28px] md:text-[36px] font-bold text-[#111111] tracking-tight">
                  More kits to explore.
                </h2>
              </div>
              <Link
                to="/products"
                className="inline-flex items-center gap-1 font-manrope text-sm text-[#111111] hover:text-[#2563EB] transition-colors border-b border-[#E7E7E2] hover:border-[#2563EB] pb-0.5"
              >
                View all products
                <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  className="group bg-white p-7 flex flex-col min-h-[220px] hover:bg-[#F7F7F4] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-[#111111]/45">
                      {p.no}
                    </span>
                    <ArrowUpRight
                      size={16}
                      className="text-[#111111]/30 group-hover:text-[#2563EB] transition-colors"
                    />
                  </div>
                  <div className="mt-6 font-sora text-[20px] font-semibold text-[#111111] tracking-tight">
                    {p.name}
                  </div>
                  <div className="mt-2 font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
                    {p.tagline}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SectionRule />
      <Footer />
      <StickyBuyBar product={product} />
    </main>
  );
};

export default ProductDetail;
